package pl.decks.personal.pages.ievp.sections.background

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import pl.decks.personal.components.animations.FlickerAnimation
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout


object BackgroundSectionStylesheet {
    object Vars {
        val mainIconSize by StyleVariable(3.cssRem)
        val entryAccentColor by StyleVariable<Color>()
        val drawBackgroundColor by StyleVariable<Color>()

        val canvasDrawStart by StyleVariable(SiteLayout.Space.XLarge)
        val promptAnimationDuration by StyleVariable(5 * TransitionDurationVars.UltraSlow.value())
    }

    object Facet {
        val Container = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .gap(SiteLayout.Space.Large)
        }
        val RoleHeading = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.Small)
                .margin(top = Vars.canvasDrawStart.value())
        }
        val IconHolder = CssStyle.base {
            Modifier
                .size(2 * Vars.mainIconSize.value())
                .flexShrink(0)
                .flexGrow(0)
                .fontSize(Vars.mainIconSize.value())
        }
        val Icon = CssStyle.base {
            Modifier
                .borderRadius(SiteLayout.Rounding.Full)
                .border(
                    width = SiteLayout.Line.Thick,
                    style = LineStyle.Solid,
                    color = Vars.entryAccentColor.value(colorMode.toPalette().color)
                )
                .backgroundColor(Vars.drawBackgroundColor.value(Colors.Transparent))
                .overflow(Overflow.Clip)
                .color(Vars.entryAccentColor.value(colorMode.toPalette().color))
        }
        val Title = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontWeight(FontWeight.Bold)
                .fontSize(FontTypography.Size.XLarge)
                .color(Vars.entryAccentColor.value(colorMode.toPalette().color))
        }
        val Flavour = FontTypography.serif.extendedByBase {
            Modifier
                .fillMaxWidth()
                .fontStyle(FontStyle.Italic)
                .textAlign(TextAlign.Center)
        }
    }

    object Proof {
        val EntryList = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.Medium)
        }
        val Entry = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.XXSmall)
        }
        val EntryIcon = CssStyle.base {
            Modifier
                .color(Vars.entryAccentColor.value(colorMode.toPalette().color))
                .fontSize(FontTypography.Size.XXXLarge)
                .flexShrink(0)
        }
        val InlineIcon = CssStyle.base {
            Modifier
                .color(Vars.entryAccentColor.value(colorMode.toPalette().color))
        }
        val What = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontWeight(FontTypography.Weight.Medium)
                .fontSize(FontTypography.Size.Relative.Larger)
        }
        val Where = FontTypography.serif.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Relative.Smaller)
        }
        val SecondaryInfo = FontTypography.mono.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Relative.Smaller)
                .fillMaxWidth()
        }
        val Description = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fillMaxWidth()
                .fontStyle(FontStyle.Italic)
                .fontWeight(FontTypography.Weight.Light)
                .fontSize(FontTypography.Size.Relative.Smaller)
        }
    }

    val Section = CssStyle.base {
        Modifier.fillMaxWidth()
            .setVariable(Vars.entryAccentColor, colorMode.toSitePalette().accents.primary)
            .setVariable(Vars.drawBackgroundColor, colorMode.toSitePalette().surface)
    }
    val StickyCanvas = CssStyle.base {
        Modifier
            .position(Position.Sticky)
            .top(SiteLayout.Offset.none)
            .fillMaxWidth()
            .height(SiteLayoutStylesheet.Vars.approxScreenSpace.value())
    }
    val ScrollPrompt = CssStyle.base {
        Modifier
            .fillMaxWidth()
            .animation(
                FlickerAnimation.Frames.toAnimation(
                    duration = Vars.promptAnimationDuration.value(),
                    timingFunction = AnimationTimingFunction.EaseInOut,
                    iterationCount = AnimationIterationCount.Infinite,
                )
            )
            .color(Vars.entryAccentColor.value(colorMode.toPalette().color))
    }
    val Divider = CssStyle.base {
        Modifier.borderTop(
            width = SiteLayout.Line.Normal,
            style = LineStyle.Solid,
            color = Vars.entryAccentColor.value(colorMode.toPalette().color),
        )
    }
}
